import { promoCodeManager } from '../../services/PromoCodeManager'
import {
  DISCOUNT_TYPES,
  PROMO_CODE_SOURCE,
} from '../../stores/promoCodeStore/promoCode.consts'
import { useEffect, useRef, useState } from 'react'
import { ONBOARDING_URL } from '../../config/config'
import {
  booksBannerManager,
  genericBannerManager,
} from '../../services/GenericBannerManager'
import { useLocation } from 'react-router'
import {
  hasStoredPromoCode,
  removeStoredPromoCode,
} from '../../services/promoCodeStore'
import {
  getPendingCouponDialog,
  removePendingCouponDialog,
  setPendingCouponDialog,
} from './pendingCouponDialog'
import useBundleDiscount from '../../hooks/useBundleDiscount'
import {
  MATERIAL_TYPES,
  TILE_SIZES,
  getSizesData,
  BookSize,
} from '@mixtiles/web-backend-shared'
import { formatPriceWithPrecision } from '../../utils/utils'
import { getDiscountSubtitle } from '../../utils/discountSubtitle'
import { translateManager as t } from '../../services/TranslateManager'
import { getSizeName } from '../../services/SizesManager'
import { getGiftCardDialogText } from '../PromoCodeDialog/giftCardDialogUtils'
import {
  ICON_SIZES,
  SNACKBAR_VARIANTS,
} from '../../elements/Snackbar/Snackbar.consts'
import { useSnackbar } from 'notistack'
import useDialog from '../../elements/Dialog/DialogProvider'
import GiftCardSuccessDialog from '../PromoCodeDialog/GiftCardSuccessDialog/GiftCardSuccessDialog'
import { ReactComponent as TagPinkSVG } from '../../icons/assets/TagPink.svg'
import { useEmbeddedLogin } from '../EmbeddedLogin/EmbeddedLoginProvider'
import { getBestDiscountFromMixType } from '../../services/promoCodeTextGenerator'
import { useProductType } from '../../services/ProductTypesManager'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { usePricing } from '../Pricing/PricingProvider'

function PromoCodeDialogContainer() {
  // This handles cases when a promo code is loaded and updated in the promoCodeManager prior to mounting this component.
  // From that point onwards the subscription in useEffect covers us.
  const snackbarShownOnce = useRef(false)
  const location = useLocation()
  const [{ coupon, hideCouponPopup, source }, setCouponConfig] = useState({})
  const dialog = useDialog()
  const subscriptionRef = useRef()
  const { fetchDiscount, fetchPricingItem } = usePricing()
  const { productType } = useProductType()

  let pricingProduct

  switch (productType) {
    case PRODUCT_TYPES.PHOTO_BOOK:
      pricingProduct = {
        tileSize: BookSize.BOOK_8X8,
      }
      break
    case PRODUCT_TYPES.BUSINESS: {
      pricingProduct = null
      break
    }
    default: {
      pricingProduct = {
        tileSize: TILE_SIZES.SQUARE_8X8,
        materialType: MATERIAL_TYPES.CLASSIC,
      }
    }
  }

  const { isLoading: isLoadingDiscount, originalBundlePrice } =
    useBundleDiscount({
      tileSize: TILE_SIZES.SQUARE_8X8,
      materialType: MATERIAL_TYPES.CLASSIC,
    })

  let pricingData
  let discount
  if (pricingProduct) {
    pricingData = fetchPricingItem(pricingProduct, 'PromoCodeDialogContainer')
    discount = fetchDiscount(pricingProduct)
  }

  const { enqueueSnackbar } = useSnackbar()
  const { isOpen: isEmbeddedLoginOpen } = useEmbeddedLogin()

  const loadCoupon = (couponData) => {
    let autoCouponLoad
    let hideCouponPopup = false
    if (couponData) {
      const { coupon, couponTitle, source } = couponData
      hideCouponPopup = !!couponData.hideCouponPopup
      // Prevent coupon popup if the user is on the homepage and the coupon was loaded from the banner
      if (source === PROMO_CODE_SOURCE.BANNER && location.pathname === '/') {
        return
      }
      if (coupon !== promoCodeManager.getDiscountCouponCode()) {
        autoCouponLoad = promoCodeManager.autoLoadOfGivenCoupon(
          coupon,
          couponTitle,
          source
        )
      }
    } else if (!promoCodeManager.getDiscountCoupon() || hasStoredPromoCode()) {
      autoCouponLoad = promoCodeManager.autoLoadOfCoupon()
      removeStoredPromoCode()
    }

    if (autoCouponLoad) {
      autoCouponLoad
        .then(({ coupon, couponTitle, source }) => {
          setPendingCouponDialog({ coupon, couponTitle, hideCouponPopup })
          setCouponConfig({ coupon, couponTitle, hideCouponPopup, source })
        })
        .catch(() => {
          // Failed loading coupon
        })
    }
  }

  const isHomepage = location.pathname === '/'
  useEffect(() => {
    promoCodeManager.removeAllExpiredDiscountCoupons()
    if (getPendingCouponDialog()) {
      const { coupon, couponTitle, hideCouponPopup } = getPendingCouponDialog()
      setCouponConfig({ coupon, couponTitle, hideCouponPopup, source })
    }

    subscriptionRef.current = promoCodeManager.couponAutoLoadSubject.subscribe(
      (couponData) => {
        loadCoupon(couponData)
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomepage])

  const displaySnackBar = () => {
    const price = (value) =>
      formatPriceWithPrecision(value, pricingData.currency)
    const discountValue = discount.value
    const title = t.get('general.menu.add_promo_code.success.code_applied', {
      v1: coupon.code,
    })
    let subtitle
    if (coupon.discountType === DISCOUNT_TYPES.UNIT) {
      const sizesData = getSizesData()

      const defaultSizeConstraintText = getSizeName(TILE_SIZES.SQUARE_8X8)
      const sizeConstraintText = `(${
        (discount?.metadata?.tileSizesList?.[0] &&
          sizesData[discount?.metadata.tileSizesList[0]] &&
          getSizeName(discount?.metadata.tileSizesList[0])) ||
        defaultSizeConstraintText
      })`

      subtitle = t.get(
        'general.promo_code_success_dialog.title.free_tiles_many',
        {
          v1: coupon.amount,
          size_constraint: sizeConstraintText,
        }
      )
    } else if (coupon.discountType === DISCOUNT_TYPES.GIFT_CARD) {
      subtitle = t.get('general.promo_code_success_dialog.subtitle.gift_card', {
        v1: formatPriceWithPrecision(
          coupon.metadata.remainingValue,
          coupon.metadata.currency
        ),
      })
    } else if (coupon.discountType === DISCOUNT_TYPES.MIX) {
      const bestDiscount = getBestDiscountFromMixType(
        coupon,
        'product_selection.tiles_name',
        fetchPricingItem,
        price,
        productType,
        pricingData.currency
      )
      subtitle = bestDiscount.text
    } else {
      if (coupon.discountType === DISCOUNT_TYPES.BUYXGETY) {
        const couponData = promoCodeManager.getDiscountCoupon()
        couponData.metadata.percentOff = Math.round(
          (discountValue.metadata.get * 100) /
            (discountValue.metadata.buy + discountValue.metadata.get)
        )
        promoCodeManager.editDiscountCoupon(couponData)
      }
      subtitle = getDiscountSubtitle(
        discountValue,
        originalBundlePrice,
        t.get,
        pricingData.currency,
        { allTiers: true }
      )
    }
    enqueueSnackbar('', {
      variant: SNACKBAR_VARIANTS.COMMON,
      title,
      subtitle,
      icon: <TagPinkSVG />,
      iconSize: ICON_SIZES.BIG,
      name: 'Promo Code',
    })
  }

  useEffect(() => {
    // Prevent promo code popup during onboarding. Instead, postpone and show it once
    // the onboarding is finished. It is possible due to the fact that the current
    // effect runs on every path change
    if (location.pathname?.startsWith(ONBOARDING_URL)) {
      return
    }
    if (isEmbeddedLoginOpen) {
      return
    }

    promoCodeManager.startMonitoringTime()
    if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
      booksBannerManager.ensureBannerPromoIsApplied()
    } else {
      genericBannerManager.ensureBannerPromoIsApplied()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, productType])

  const onCouponShown = () => {
    // Prevent showing the popup again after was viewed once
    snackbarShownOnce.current = true
    removePendingCouponDialog()
  }

  useEffect(() => {
    if (
      snackbarShownOnce.current ||
      location.pathname?.startsWith(ONBOARDING_URL)
    ) {
      return
    }

    const isGiftCard =
      coupon?.discountType === DISCOUNT_TYPES.GIFT_CARD ||
      (coupon?.discountType === DISCOUNT_TYPES.UNIT &&
        coupon?.metadata?.isGiftCard)

    if (isGiftCard) {
      const { title, subtitle } = getGiftCardDialogText(coupon)

      dialog.open(
        <GiftCardSuccessDialog
          open
          onClose={() => dialog.close()}
          title={title}
          subtitle={subtitle}
          onDismiss={() => dialog.close()}
        />
      )
      onCouponShown()
    } else if (
      coupon?.code &&
      !isLoadingDiscount &&
      !hideCouponPopup &&
      discount?.value?.promoCode === coupon?.code
    ) {
      displaySnackBar()
      onCouponShown()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, isLoadingDiscount, discount, location.pathname])

  return null
}

export default PromoCodeDialogContainer
